import { Mars, NonBinary, Venus } from "lucide-react";

import type { Contact, ContactGender } from "../types";

export const defaultEmailJsonb = [{ email: null, type: null }];
export const defaultPhoneJsonb = [{ number: null, type: null }];

const cleanContactArrayFields = (data: Contact) => {
  const cleanedEmailJsonb =
    data.email_jsonb?.filter((e) => e.email != null) || [];
  const cleanedPhoneJsonb =
    data.phone_jsonb?.filter((p) => p.number != null) || [];
  return {
    ...data,
    phone_jsonb: cleanedPhoneJsonb.length > 0 ? cleanedPhoneJsonb : null,
    email_jsonb: cleanedEmailJsonb.length > 0 ? cleanedEmailJsonb : null,
  };
};

export const cleanupContactForCreate = (data: Contact) => {
  return cleanContactArrayFields({
    ...data,
    first_seen: new Date().toISOString(),
    last_seen: new Date().toISOString(),
    tags: [],
  });
};

export const cleanupContactForEdit = cleanContactArrayFields;

// Sprint 10.x — Fabrik-1 crée un contact à l'inscription avec
// first_name='Pending' / last_name='Pending' avant que l'user complète son
// profil. Helper pour afficher l'email à la place du nom dans ces cas.
//
// Retourne :
//   - "First Last" si nom rempli (premier mot != Pending)
//   - sinon le primary email
//   - sinon "Contact #<id>" en dernier recours
export function getContactDisplayName(
  contact: Pick<Contact, "first_name" | "last_name" | "email_jsonb" | "id">,
): string {
  const fn = (contact.first_name ?? "").trim();
  const ln = (contact.last_name ?? "").trim();
  const isPending = fn.toLowerCase() === "pending" || (!fn && !ln);
  if (!isPending && (fn || ln)) {
    return `${fn}${fn && ln ? " " : ""}${ln}`.trim();
  }
  const primaryEmail = contact.email_jsonb?.[0]?.email;
  if (primaryEmail) return primaryEmail;
  return `Contact #${contact.id}`;
}

type TranslateFn = (key: string, options?: { [key: string]: any }) => string;

export const contactGenderDefaultLabels: Record<string, string> = {
  male: "He/Him",
  female: "She/Her",
  nonbinary: "They/Them",
};

const personalInfoTypeMap: Record<string, string> = {
  Work: "work",
  Home: "home",
  Other: "other",
};

export const contactGender: ContactGender[] = [
  {
    value: "male",
    label: "resources.contacts.inputs.genders.male",
    icon: Mars,
  },
  {
    value: "female",
    label: "resources.contacts.inputs.genders.female",
    icon: Venus,
  },
  {
    value: "nonbinary",
    label: "resources.contacts.inputs.genders.nonbinary",
    icon: NonBinary,
  },
];

export const translateContactGenderLabel = (
  gender: { value: string; label: string },
  translate: TranslateFn,
) =>
  translate(gender.label, {
    _: contactGenderDefaultLabels[gender.value] ?? gender.label,
  });

export const translatePersonalInfoTypeLabel = (
  type: string,
  translate: TranslateFn,
) =>
  translate(
    `resources.contacts.inputs.personal_info_types.${personalInfoTypeMap[type] ?? type.toLowerCase()}`,
    {
      _: type,
    },
  );
